import { useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useResponsiveStore } from "@/stores/editor/responsive-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { logger } from "@/utils";
import type { OwnerContext } from "@/services/api/projects";

const buildProjectViewerPath = (
  projectId: string,
  ownerContext?: OwnerContext,
  pageId?: string,
) => {
  const pagePath = pageId ? `/page/${pageId}` : "";

  if (ownerContext?.type === "organization" && ownerContext.orgSlug) {
    return `/orgs/${ownerContext.orgSlug}/viewer/${projectId}${pagePath}`;
  }

  if (ownerContext?.type === "user" && ownerContext.username) {
    return `/users/${ownerContext.username}/viewer/${projectId}${pagePath}`;
  }

  return `/viewer/${projectId}${pagePath}`;
};

const isNonNullRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

interface RestoredEditorState {
  components: Array<Record<string, unknown>>;
  selectedComponents: string[];
  activeViewport: string;
  timestamp: string;
}

const isComponentSnapshot = (value: unknown): value is Record<string, unknown> =>
  isNonNullRecord(value) &&
  typeof value.id === "string" &&
  typeof value.type === "string";

const isRestoredEditorState = (value: unknown): value is RestoredEditorState =>
  isNonNullRecord(value) &&
  Array.isArray(value.components) &&
  value.components.every(isComponentSnapshot) &&
  Array.isArray(value.selectedComponents) &&
  value.selectedComponents.every((componentId) => typeof componentId === "string") &&
  typeof value.activeViewport === "string" &&
  typeof value.timestamp === "string";

/**
 * Central integration hook for managing seamless user flows
 * Coordinates authentication, project workflows, and editor state
 */
export const useUserFlowIntegration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const { validateSession, extendSession, getSessionStatus } = useAuthSession();
  const { components, selectedComponents } = useEditorStore();
  const { activeViewport } = useResponsiveStore();

  // Track user activity for session management
  const lastActivityRef = useRef<Date>(new Date());
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Handle user activity to extend session
   */
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = new Date();
    extendSession();
  }, [extendSession]);

  /**
   * Set up activity tracking listeners
   */
  useEffect(() => {
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
  }, [handleUserActivity]);

  /**
   * Periodic session validation
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    // Clear existing interval
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }

    // Set up periodic session check
    sessionCheckIntervalRef.current = setInterval(async () => {
      const isValid = await validateSession();
      if (!isValid) {
        logger.warn("Session is no longer valid during user flow");
        navigate("/login", { replace: true });
      }
    }, 60000); // Check every minute

    return () => {
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
    };
  }, [isAuthenticated, validateSession, navigate]);

  /**
   * Handle route transitions with proper authentication checks
   */
  const navigateWithAuth = useCallback(
    async (to: string, options?: { replace?: boolean }) => {
      // Validate session before navigation
      const isValid = await validateSession();
      if (!isValid) {
        navigate("/login", { replace: true });
        return false;
      }

      navigate(to, options);
      return true;
    },
    [validateSession, navigate],
  );

  /**
   * Enhanced project opening with state management
   */
  const openProject = useCallback(
    async (
      projectId: string,
      ownerContext?: OwnerContext,
      pageId?: string,
    ) => {
      try {
        const targetUrl = buildProjectViewerPath(
          projectId,
          ownerContext,
          pageId,
        );

        // Debug logging to track project navigation
        console.log("🎯 [USER_FLOW] Opening project:", {
          requestedProjectId: projectId,
          currentUrl: window.location.href,
          targetUrl,
          ownerContext,
          userId: user?.id,
        });

        // Extend session
        extendSession();

        // Validate session
        const isValid = await validateSession();
        if (!isValid) {
          navigate("/login", { replace: true });
          return false;
        }

        // Navigate to viewer (default mode)
        navigate(targetUrl);

        logger.info("Project opened successfully", {
          projectId,
          ownerContext,
          userId: user?.id,
        });
        return true;
      } catch (error) {
        logger.error("Failed to open project:", error);
        return false;
      }
    },
    [extendSession, validateSession, navigate, user?.id],
  );

  /**
   * Enhanced project creation with immediate editor transition
   */
  const createAndOpenProject = useCallback(
    async (
      projectData: {
        name: string;
        description?: string;
        isPrivate?: boolean;
      },
      explicitOwnerContext?: OwnerContext,
    ) => {
      try {
        // Extend session
        extendSession();

        // Validate session
        const isValid = await validateSession();
        if (!isValid) {
          navigate("/login", { replace: true });
          return null;
        }

        // Import ProjectsApi dynamically to avoid circular dependencies
        const { ProjectsApi } = await import("@/services/api/projects");

        // Use explicit owner context when provided; otherwise preserve the
        // existing persisted organization-context fallback.
        let requestOwnerContext = explicitOwnerContext;
        if (!requestOwnerContext) {
          const { currentContext: ctx } = useOrganizationStore.getState();
          if (ctx?.type === "organization" && ctx.orgSlug) {
            requestOwnerContext = {
              type: "organization",
              orgSlug: ctx.orgSlug,
            };
          }
        }

        // Create project by calling the backend API (always multi-page)
        const response = await ProjectsApi.createProject(
          {
            name: projectData.name,
            description: projectData.description,
            isPrivate: projectData.isPrivate,
            isMultiPage: true,
            initialPages: [
              {
                name: "Home",
                description: "Main overview and dashboard",
                template: "BLANK",
                isDefault: true,
              },
            ],
          },
          requestOwnerContext,
        );

        if (!response.success || !response.data) {
          logger.error("Failed to create project:", response.message);
          return null;
        }

        const projectId = response.data.id;

        if (!projectId) {
          logger.error("Project created but no ID returned:", response.data);
          return null;
        }

        // Navigate using the same owner context that scoped the creation call.
        navigate(buildProjectViewerPath(projectId, requestOwnerContext));

        logger.info("Project created and opened", {
          projectId,
          ownerContext: requestOwnerContext,
          ...projectData,
        });
        return projectId;
      } catch (error) {
        logger.error("Failed to create and open project:", error);
        return null;
      }
    },
    [extendSession, validateSession, navigate],
  );

  /**
   * Handle viewport switching with session extension
   */
  const switchViewport = useCallback(
    (viewportId: string) => {
      extendSession();
      logger.debug("Viewport switched", { viewportId, userId: user?.id });
    },
    [extendSession, user?.id],
  );

  /**
   * Handle collaboration events with authentication
   */
  const joinCollaboration = useCallback(
    async (projectId: string) => {
      try {
        // Validate session before joining collaboration
        const isValid = await validateSession();
        if (!isValid) {
          logger.warn("Cannot join collaboration: invalid session");
          return false;
        }

        extendSession();
        logger.info("Joined collaboration session", {
          projectId,
          userId: user?.id,
        });
        return true;
      } catch (error) {
        logger.error("Failed to join collaboration:", error);
        return false;
      }
    },
    [validateSession, extendSession, user?.id],
  );

  /**
   * Handle editor state persistence
   */
  const saveEditorState = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      extendSession();

      const editorState = {
        components,
        selectedComponents,
        activeViewport,
        timestamp: new Date(),
      };

      // Save to localStorage as backup
      localStorage.setItem(
        "terramatrix_editor_state",
        JSON.stringify(editorState),
      );

      logger.debug("Editor state saved", { componentCount: components.length });
      return true;
    } catch (error) {
      logger.error("Failed to save editor state:", error);
      return false;
    }
  }, [
    isAuthenticated,
    extendSession,
    components,
    selectedComponents,
    activeViewport,
  ]);

  /**
   * Restore editor state
   */
  const restoreEditorState = useCallback(() => {
    try {
      const savedState = localStorage.getItem("terramatrix_editor_state");
      if (savedState) {
        const editorState: unknown = JSON.parse(savedState);
        if (!isRestoredEditorState(editorState)) {
          return null;
        }

        const state = editorState;
        logger.debug("Editor state restored", {
          timestamp: state.timestamp,
        });
        return state;
      }
    } catch (error) {
      logger.error("Failed to restore editor state:", error);
    }
    return null;
  }, []);

  /**
   * Handle graceful logout with cleanup
   */
  const logout = useCallback(() => {
    // Save current state before logout
    saveEditorState();

    // Clear intervals
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
    }

    // Navigate to login
    navigate("/login", { replace: true });

    logger.info("User logged out gracefully");
  }, [saveEditorState, navigate]);

  /**
   * Get current user flow status
   */
  const getUserFlowStatus = useCallback(() => {
    const sessionStatus = getSessionStatus();

    return {
      isAuthenticated,
      sessionValid: sessionStatus.isValid,
      sessionNearExpiration: sessionStatus.isNearExpiration,
      currentPath: location.pathname,
      lastActivity: lastActivityRef.current,
      user: user
        ? {
            id: user.id,
            name: user.firstName
              ? `${user.firstName} ${user.lastName}`
              : user.email,
            email: user.email,
          }
        : null,
    };
  }, [isAuthenticated, getSessionStatus, location.pathname, user]);

  return {
    // Navigation
    navigateWithAuth,
    openProject,
    createAndOpenProject,

    // Editor integration
    switchViewport,
    saveEditorState,
    restoreEditorState,

    // Collaboration
    joinCollaboration,

    // Session management
    handleUserActivity,
    logout,

    // Status
    getUserFlowStatus,
  };
};

export default useUserFlowIntegration;
